import { connect } from "react-redux";
import React, { useRef, useState, useEffect } from 'react';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import * as THREE from 'three';
import { v4 as uuidv4 } from 'uuid';
import { mockupCreate } from 'actions/mockupAction';
import Loading from 'components/Loading';

const PRODUCT_OPTIONS = {
    "T-SHIRT": ["BLACK", "WHITE", "SPORT GREY", "SAND", "FOREST GREEN", "LIGHT PINK", "NAVY", "MILITARY GREEN", "MAROON", "RED", "ROYAL BLUE", "ASH GREY", "LIGHT BLUE", "CHARCOAL", "DARK HEATHER", "PURPLE", "ORANGE"],
    "YOUTH T-SHIRT": ["BLACK", "WHITE", "IRISH GREEN", "LIGHT PINK", "FOREST GREEN"],
    "SWEATSHIRT": ["BLACK", "WHITE", "SPORT GREY", "SAND", "FOREST GREEN", "LIGHT PINK", "NAVY", "MILITARY GREEN", "MAROON", "RED", "ROYAL BLUE", "ASH GREY", "ORANGE", "DARK HEATHER"],
    "HOODIE": ["BLACK", "WHITE", "SPORT GREY", "SAND", "FOREST GREEN", "LIGHT PINK", "NAVY"],
    "TANK TOP": ["BLACK", "WHITE", "SPORT GREY", "NAVY"],
    "COMFORT COLOR": ["BLACK", "WHITE", "PEPPER", "IVORY", "ESPRESSO", "BLUE SPRUCE", "CRIMSOM", "BAY", "MOSS", "BLUE JEAN", "SEAFOAM", "GREY", "BLOSSOM", "GRAPHITE", "VIOLET"],
    "WASHED": ["BLACK", "GREY"],
    "MUG": ["BLACK", "WHITE"],
    "POSTER-CANVAS": ["BLACK", "WHITE"],
    "BLANKET": ["BLACK", "WHITE"],
    "ORNAMENT": ["BLACK", "WHITE"],
};

const COLOR_HEX = {
    "BLACK": "#000000", "WHITE": "#FFFFFF", "SPORT GREY": "#C0C0C0", "SAND": "#F5DEB3", "FOREST GREEN": "#228B22",
    "LIGHT PINK": "#FFB6C1", "NAVY": "#000080", "MILITARY GREEN": "#4B5320", "MAROON": "#800000", "RED": "#FF0000",
    "ROYAL BLUE": "#4169E1", "ASH GREY": "#B2BEB5", "LIGHT BLUE": "#ADD8E6", "CHARCOAL": "#36454F",
    "DARK HEATHER": "#A9A9A9", "PURPLE": "#800080", "ORANGE": "#FFA500", "IRISH GREEN": "#00A550",
    "PEPPER": "#3C3C3C", "IVORY": "#FFFFF0", "ESPRESSO": "#4B2E2B", "BLUE SPRUCE": "#317873", "CRIMSOM": "#DC143C",
    "BAY": "#A3C1AD", "MOSS": "#8A9A5B", "BLUE JEAN": "#5D8AA8", "SEAFOAM": "#93E9BE", "GREY": "#808080",
    "BLOSSOM": "#FFD3E0", "GRAPHITE": "#383838", "VIOLET": "#8F00FF"
};

const ZONE_COLORS = {
    print: new THREE.Color('#00ff00'),
    delete: new THREE.Color('#ff0000')
};

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const num = (v) => (v === '' || v === null || v === undefined ? 0 : Number(v));
const fnum = (v) => (v === '' || v === null || v === undefined ? 0 : parseFloat(v));

const ImageEditor3D = ({ mockupCreate }) => {
    const cropperRef = useRef(null);
    const threeCanvasRef = useRef(null);
    const [imageSrc, setImageSrc] = useState(null);
    const [croppedImage, setCroppedImage] = useState(null);
    const [fixedSize, setFixedSize] = useState({ width: 0, height: 0 });
    const [zones, setZones] = useState([]);
    const [productType, setProductType] = useState("");
    const [color, setColor] = useState("");
    const [imageUrl, setImageUrl] = useState("");
    const [loading, setLoading] = useState(false);

    const sceneRef = useRef();
    const cameraRef = useRef();
    const rendererRef = useRef();
    const meshMap = useRef({});

    const finalCanvasRef = useRef(null);
    const imageRef = useRef(null);

    // Drag state
    const raycasterRef = useRef(new THREE.Raycaster());
    const pointerRef = useRef(new THREE.Vector2());
    const draggingRef = useRef(false);
    const dragZoneIdRef = useRef(null);
    const dragPlaneRef = useRef(new THREE.Plane());
    const dragOffsetRef = useRef(new THREE.Vector3());

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => setImageSrc(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleImageUrlChange = (e) => setImageUrl(e.target.value);
    const loadImageFromUrl = () => setImageSrc(imageUrl);

    const getCroppedImage = () => {
        if (!cropperRef.current) return;
        const cropper = cropperRef.current?.cropper;
        const canvas = cropper.getCroppedCanvas({ maxWidth: 1000, maxHeight: 1000, fillColor: '#fff' });
        const ratio = Math.min(1000 / canvas.width, 1000 / canvas.height);
        const finalCanvas = document.createElement('canvas');
        finalCanvas.width = Math.max(1, Math.floor(canvas.width * ratio));
        finalCanvas.height = Math.max(1, Math.floor(canvas.height * ratio));
        const ctx = finalCanvas.getContext('2d');
        ctx.drawImage(canvas, 0, 0, finalCanvas.width, finalCanvas.height);
        const url = finalCanvas.toDataURL();
        setCroppedImage(url);
        setFixedSize({ width: finalCanvas.width, height: finalCanvas.height });
    };

    const addZone = (type, id) => {
        const safeType = type === 'delete' ? 'delete' : 'print';
        if (id) {
            const base = zones.find(z => z.id === id);
            if (base) {
                setZones(prev => [...prev, {
                    ...base,
                    id: uuidv4(),
                    type: safeType,
                    posZ: base.posZ + 10,
                    width: Math.max(50, (base.width - 30)),
                    height: Math.max(50, (base.height - 30)),
                }]);
                return;
            }
        }
        setZones(prev => [...prev, {
            id: uuidv4(),
            type: safeType,
            width: 200,
            height: 200,
            shape: 'rectangle',
            rotateX: 0, rotateY: 0, rotateZ: 0,
            posX: 0, posY: 0, posZ: 0,
            warpX: 0, warpY: 0,
            watermark: { text: "COTIK", opacity: 0.5, fontSize: 24, position: { x: 20, y: 20 } }
        }]);
    };

    const updateZone = (id, newData) => {
        if (newData.rotateX !== undefined) newData.rotateX = clamp(Number(newData.rotateX), -90, 90);
        if (newData.rotateY !== undefined) newData.rotateY = clamp(Number(newData.rotateY), -90, 90);
        if (newData.rotateZ !== undefined) newData.rotateZ = clamp(Number(newData.rotateZ), -90, 90);
        if (newData.width !== undefined) newData.width = clamp(Number(newData.width), 50, 500);
        if (newData.height !== undefined) newData.height = clamp(Number(newData.height), 50, 500);
        if (newData.posX !== undefined) newData.posX = clamp(Number(newData.posX), -300, 300);
        if (newData.posY !== undefined) newData.posY = clamp(Number(newData.posY), -300, 300);
        if (newData.posZ !== undefined) newData.posZ = clamp(Number(newData.posZ), -300, 300);
        if (newData.warpX !== undefined) newData.warpX = clamp(parseFloat(newData.warpX), -3, 3);
        if (newData.warpY !== undefined) newData.warpY = clamp(parseFloat(newData.warpY), -3, 3);

        setZones(prev => prev.map(z => z.id === id ? { ...z, ...newData } : z));
    };

    const deleteZone = (id) => setZones(prev => prev.filter(z => z.id !== id));

    // Đồng bộ finalCanvas kích thước ngay khi có fixedSize
    useEffect(() => {
        if (!fixedSize.width) return;
        const c = finalCanvasRef.current;
        if (c) { c.width = fixedSize.width; c.height = fixedSize.height; }
    }, [fixedSize]);

    // Khởi tạo Three với OrthographicCamera
    useEffect(() => {
        if (!croppedImage || !fixedSize.width) return;
        const canvas = threeCanvasRef.current;
        const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        renderer.setSize(fixedSize.width, fixedSize.height);
        rendererRef.current = renderer;

        const scene = new THREE.Scene();
        sceneRef.current = scene;

        // Ortho camera: trùng hệ toạ độ ảnh nền
        const halfW = fixedSize.width / 2;
        const halfH = fixedSize.height / 2;
        const camera = new THREE.OrthographicCamera(-halfW, halfW, halfH, -halfH, 0.1, 2000);
        camera.position.set(0, 0, 1000);
        camera.lookAt(0, 0, 0);
        cameraRef.current = camera;

        scene.add(new THREE.AmbientLight(0xffffff, 1.2));
        const directionalLight = new THREE.DirectionalLight(0xffffff, 0.5);
        directionalLight.position.set(100, 100, 100);
        scene.add(directionalLight);

        renderer.render(scene, camera);

        return () => {
            renderer.dispose();
        };
    }, [croppedImage, fixedSize]);

    // Vẽ lại mỗi khi zones thay đổi
    useEffect(() => {
        if (!sceneRef.current || !cameraRef.current || !rendererRef.current) return;
        const scene = sceneRef.current;
        const camera = cameraRef.current;
        const renderer = rendererRef.current;

        // Dọn meshes cũ
        Object.values(meshMap.current).forEach(m => {
            m.geometry?.dispose();
            m.material?.dispose();
            scene.remove(m);
        });
        meshMap.current = {};

        // Print trước, Delete sau
        const orderedZones = [...zones.filter(z => z.type === 'print'), ...zones.filter(z => z.type === 'delete')];

        orderedZones.forEach((zone) => {
            let geometry;

            if (zone.shape === 'circle') {
                const radius = Math.min(zone.width, zone.height) / 2;
                geometry = new THREE.CircleGeometry(radius, 64);
            } else if (zone.shape === 'triangle') {
                const w = zone.width / 2;
                const h = zone.height / 2;
                const vertices = new Float32Array([
                    0, h, 0,
                    -w, -h, 0,
                    w, -h, 0
                ]);
                geometry = new THREE.BufferGeometry();
                geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
                geometry.computeVertexNormals();
            } else if (zone.shape === 'ellipse') {
                const radiusX = zone.width / 2;
                const radiusY = zone.height / 2;
                const curve = new THREE.EllipseCurve(0, 0, radiusX, radiusY, 0, 2 * Math.PI, false, 0);
                const points = curve.getPoints(64);
                const shape = new THREE.Shape(points);
                geometry = new THREE.ShapeGeometry(shape);
            } else {
                geometry = new THREE.PlaneGeometry(zone.width, zone.height, 50, 50);
            }

            // Warp chỉ cho print
            if (zone.type !== 'delete') {
                const pos = geometry.attributes.position;
                const ampX = zone.warpX || 0, ampY = zone.warpY || 0;
                for (let i = 0; i < pos.count; i++) {
                    const x = pos.getX(i), y = pos.getY(i);
                    const wave = Math.sin(x * 0.1) * ampX + Math.cos(y * 0.1) * ampY;
                    pos.setZ(i, wave);
                }
                pos.needsUpdate = true;
                geometry.computeVertexNormals();
            }

            const material = new THREE.MeshStandardMaterial({
                color: ZONE_COLORS[zone.type] || new THREE.Color('#0000ff'),
                transparent: true,
                opacity: 0.9,
                roughness: 0.7,
                metalness: 0.1,
                side: THREE.DoubleSide,
            });

            const mesh = new THREE.Mesh(geometry, material);
            mesh.rotation.set(
                THREE.MathUtils.degToRad(zone.rotateX || 0),
                THREE.MathUtils.degToRad(zone.rotateY || 0),
                THREE.MathUtils.degToRad(zone.rotateZ || 0)
            );
            mesh.position.set(zone.posX || 0, zone.posY || 0, zone.posZ || 0);
            mesh.renderOrder = zone.type === 'delete' ? 2 : 1;

            scene.add(mesh);
            meshMap.current[zone.id] = mesh;
        });

        // Render 2 tick để ổn định
        renderer.render(scene, camera);
        requestAnimationFrame(() => {
            renderer.render(scene, camera);
            // Composite lên final canvas
            const finalCanvas = finalCanvasRef.current;
            const ctx = finalCanvas?.getContext('2d');
            if (ctx && imageRef.current) {
                ctx.clearRect(0, 0, finalCanvas.width, finalCanvas.height);
                ctx.drawImage(imageRef.current, 0, 0, finalCanvas.width, finalCanvas.height);
                ctx.drawImage(renderer.domElement, 0, 0, finalCanvas.width, finalCanvas.height);
            }
        });
    }, [zones]);

    // Dragging trên canvas (posX/posY)
    useEffect(() => {
        const canvas = threeCanvasRef.current;
        const camera = cameraRef.current;
        const scene = sceneRef.current;
        const renderer = rendererRef.current;
        if (!canvas || !camera || !scene || !renderer) return;

        const getPointerNDC = (event) => {
            const rect = canvas.getBoundingClientRect();
            const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
            const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
            pointerRef.current.set(x, y);
        };

        const onPointerDown = (event) => {
            getPointerNDC(event);
            const raycaster = raycasterRef.current;
            raycaster.setFromCamera(pointerRef.current, camera);

            const meshes = Object.values(meshMap.current);
            const intersects = raycaster.intersectObjects(meshes, false);
            if (intersects.length > 0) {
                const { object: hitMesh, point: hitPointWorld } = intersects[0];
                const foundEntry = Object.entries(meshMap.current).find(([id, m]) => m === hitMesh);
                if (!foundEntry) return;
                const [zoneId] = foundEntry;
                dragZoneIdRef.current = zoneId;
                draggingRef.current = true;

                // Pháp tuyến theo mặt mesh
                const normal = new THREE.Vector3();
                hitMesh.getWorldDirection(normal);
                normal.normalize().negate();
                dragPlaneRef.current.setFromNormalAndCoplanarPoint(normal, hitPointWorld.clone());

                dragOffsetRef.current.copy(hitMesh.position).sub(hitPointWorld);
                canvas.style.cursor = 'grabbing';
            } else {
                dragZoneIdRef.current = null;
                draggingRef.current = false;
            }
        };

        const onPointerMove = (event) => {
            getPointerNDC(event);
            const raycaster = raycasterRef.current;
            raycaster.setFromCamera(pointerRef.current, camera);

            if (!draggingRef.current) {
                const meshes = Object.values(meshMap.current);
                const intersects = raycaster.intersectObjects(meshes, false);
                canvas.style.cursor = intersects.length > 0 ? 'grab' : 'default';
                return;
            }

            const plane = dragPlaneRef.current;
            const hitPoint = new THREE.Vector3();
            if (raycaster.ray.intersectPlane(plane, hitPoint)) {
                const newPos = hitPoint.add(dragOffsetRef.current);
                const zoneId = dragZoneIdRef.current;
                if (zoneId) updateZone(zoneId, { posX: newPos.x, posY: newPos.y });
            }
        };

        const endDrag = () => {
            draggingRef.current = false;
            dragZoneIdRef.current = null;
            canvas.style.cursor = 'default';
        };

        canvas.addEventListener('pointerdown', onPointerDown);
        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', endDrag);

        return () => {
            canvas.removeEventListener('pointerdown', onPointerDown);
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', endDrag);
        };
    }, [croppedImage, fixedSize, zones]);

    // Khi ảnh nền load xong, vẽ lại finalCanvas
    useEffect(() => {
        if (!croppedImage || !fixedSize.width) return;
        const img = imageRef.current;
        const draw = () => {
            const finalCanvas = finalCanvasRef.current;
            const ctx = finalCanvas?.getContext('2d');
            if (ctx && img) {
                ctx.clearRect(0, 0, finalCanvas.width, finalCanvas.height);
                ctx.drawImage(img, 0, 0, finalCanvas.width, finalCanvas.height);
                if (rendererRef.current) {
                    ctx.drawImage(rendererRef.current.domElement, 0, 0, finalCanvas.width, finalCanvas.height);
                }
            }
        };
        if (img && img.complete) draw();
        else if (img) img.onload = draw;
    }, [croppedImage, fixedSize]);

    const generateImageWith3D = async () => {
        if (rendererRef.current && sceneRef.current && cameraRef.current) {
            rendererRef.current.render(sceneRef.current, cameraRef.current);
        }
        return new Promise((resolve) => {
            finalCanvasRef.current.toBlob(resolve, "image/png");
        });
    };

    const handleSave = async () => {
        if (!croppedImage || !productType || !color || zones.length === 0) return;
        setLoading(true);
        const croppedBlob = await fetch(croppedImage).then(res => res.blob());
        const blobWithZones = await generateImageWith3D();
        const formData = new FormData();
        formData.append('image', croppedBlob, 'cropped.png');
        formData.append('imageWithZones', blobWithZones, 'preview.png');
        formData.append('zones', JSON.stringify(zones));
        formData.append('productType', productType);
        formData.append('color', color);
        formData.append('size', JSON.stringify(fixedSize));
        await mockupCreate(formData);
        setLoading(false);
    };

    return (
        <div className="">
            <Loading isLoading={loading} />
            <div className="row">
                <div className="col-12">
                    <h3 className="mt-4">Create mockup</h3>
                </div>
            </div>
            <div className="row">
                {!croppedImage && (
                    <div className="col">
                        <div className="mb-3">
                            <label htmlFor="imageUrl" className="form-label">URL Image</label>
                            <input
                                type="text"
                                id="imageUrl"
                                className="form-control"
                                placeholder="URL Image"
                                value={imageUrl}
                                onChange={handleImageUrlChange}
                            />
                        </div>
                        <button className="btn btn-info mb-3" onClick={loadImageFromUrl}>Load URL</button>
                        <div className="mb-3">
                            <label htmlFor="imageFile" className="form-label">Or Select file</label>
                            <input
                                type="file"
                                className="form-control"
                                onChange={handleImageChange}
                            />
                        </div>

                        <div>
                            <Cropper
                                src={imageSrc}
                                ref={cropperRef}
                                style={{ height: 400, width: '100%' }}
                                aspectRatio={0}
                                guides={true}
                                viewMode={1}
                            />
                            {imageSrc && <button className="btn btn-primary mt-3" onClick={getCroppedImage}>Crop</button>}
                        </div>
                    </div>
                )}

                {croppedImage && (
                    <>
                        <div className="col">
                            <div style={{ position: 'relative', width: fixedSize.width, height: fixedSize.height }}>
                                <img ref={imageRef} src={croppedImage} style={{ width: '100%', height: '100%' }} alt="Cropped" />
                                <canvas ref={threeCanvasRef} style={{ position: 'absolute', top: 0, left: 0 }} />
                            </div>
                        </div>
                    </>
                )}

                {croppedImage && (
                    <>
                        <div className="col">
                            <div className="d-flex align-items-center">
                                <button className="btn btn-primary mt-3 me-5" onClick={() => addZone('print')}>Add Print Area</button>
                                <span>Adjust using the parameters below or drag directly on the image</span>
                            </div>

                            <div className="d-flex gap-2 mt-3" style={{ flexWrap: 'wrap' }}>
                                {zones.map((z, i) => (
                                    <div key={z.id} style={{ marginTop: 10, border: "1px solid #cecece", padding: 10, borderRadius: 5, minWidth: 480 }}>
                                        <strong>{z.type === 'delete' ? `Delete Area #${i + 1}` : `Print Area #${i + 1}`}</strong>

                                        <div className="form-group mt-2">
                                            <label>Zone shape</label>
                                            <select
                                                className="form-control"
                                                value={z.shape || 'rectangle'}
                                                onChange={(e) => updateZone(z.id, { shape: e.target.value })}
                                            >
                                                <option value="rectangle">Rectangle</option>
                                                <option value="circle">Circle</option>
                                                <option value="triangle">Triangle</option>
                                                <option value="ellipse">Ellipse</option>
                                            </select>
                                        </div>

                                        {["X", "Y", "Z"].map(axis => (
                                            <div key={axis} className="mt-2">
                                                Rotate {axis}:{' '}
                                                <input
                                                    type="number"
                                                    value={z[`rotate${axis}`]}
                                                    onChange={e => updateZone(z.id, { [`rotate${axis}`]: num(e.target.value) })}
                                                    style={{ width: '70px', marginRight: '10px' }}
                                                />
                                                <br />
                                                <input
                                                    type="range"
                                                    min={-90}
                                                    max={90}
                                                    value={z[`rotate${axis}`]}
                                                    onChange={e => updateZone(z.id, { [`rotate${axis}`]: num(e.target.value) })}
                                                    style={{ width: '400px' }}
                                                />
                                            </div>
                                        ))}

                                        <div className="mt-2">
                                            Position X:
                                            <input
                                                type="number"
                                                value={z.posX}
                                                min={-300}
                                                max={300}
                                                onChange={e => updateZone(z.id, { posX: num(e.target.value) })}
                                                style={{ width: '70px', marginRight: '10px' }}
                                            />
                                            <br />
                                            <input
                                                type="range"
                                                min={-300}
                                                max={300}
                                                value={z.posX}
                                                style={{ width: '400px' }}
                                                onChange={e => updateZone(z.id, { posX: num(e.target.value) })}
                                            />
                                        </div>

                                        <div className="mt-2">
                                            Position Y:
                                            <input
                                                type="number"
                                                value={z.posY}
                                                min={-300}
                                                max={300}
                                                onChange={e => updateZone(z.id, { posY: num(e.target.value) })}
                                                style={{ width: '70px', marginRight: '10px' }}
                                            />
                                            <br />
                                            <input
                                                type="range"
                                                min={-300}
                                                max={300}
                                                value={z.posY}
                                                style={{ width: '400px' }}
                                                onChange={e => updateZone(z.id, { posY: num(e.target.value) })}
                                            />
                                        </div>

                                        <div className="mt-2">
                                            Position Z:
                                            <input
                                                type="number"
                                                value={z.posZ}
                                                min={-300}
                                                max={300}
                                                onChange={e => updateZone(z.id, { posZ: num(e.target.value) })}
                                                style={{ width: '70px', marginRight: '10px' }}
                                            />
                                            <br />
                                            <input
                                                type="range"
                                                min={-300}
                                                max={300}
                                                value={z.posZ}
                                                style={{ width: '400px' }}
                                                onChange={e => updateZone(z.id, { posZ: num(e.target.value) })}
                                            />
                                        </div>

                                        <div className="mt-2">
                                            Wave X:
                                            <input
                                                type="number"
                                                value={z.warpX}
                                                step={0.5}
                                                min={-3}
                                                max={3}
                                                onChange={e => updateZone(z.id, { warpX: fnum(e.target.value) })}
                                                style={{ width: '70px', marginRight: '10px' }}
                                            />
                                            <br />
                                            <input
                                                type="range"
                                                step={0.5}
                                                min={-3}
                                                max={3}
                                                value={z.warpX}
                                                onChange={e => updateZone(z.id, { warpX: fnum(e.target.value) })}
                                                style={{ width: '400px' }}
                                            />
                                        </div>

                                        <div className="mt-2">
                                            Wave Y:
                                            <input
                                                type="number"
                                                step={0.5}
                                                value={z.warpY}
                                                min={-3}
                                                max={3}
                                                onChange={e => updateZone(z.id, { warpY: fnum(e.target.value) })}
                                                style={{ width: '70px', marginRight: '10px' }}
                                            />
                                            <br />
                                            <input
                                                type="range"
                                                step={0.5}
                                                min={-3}
                                                max={3}
                                                value={z.warpY}
                                                onChange={e => updateZone(z.id, { warpY: fnum(e.target.value) })}
                                                style={{ width: '400px' }}
                                            />
                                        </div>

                                        <div className="mt-2">
                                            Width:
                                            <input
                                                type="number"
                                                value={z.width}
                                                min={50}
                                                max={500}
                                                onChange={e => updateZone(z.id, { width: num(e.target.value) })}
                                                style={{ width: '70px', marginRight: '10px' }}
                                            />
                                            <br />
                                            <input
                                                type="range"
                                                min={50}
                                                max={500}
                                                value={z.width}
                                                onChange={e => updateZone(z.id, { width: num(e.target.value) })}
                                                style={{ width: '400px' }}
                                            />
                                        </div>

                                        <div className="mt-2">
                                            Height:
                                            <input
                                                type="number"
                                                value={z.height}
                                                min={50}
                                                max={500}
                                                onChange={e => updateZone(z.id, { height: num(e.target.value) })}
                                                style={{ width: '70px', marginRight: '10px' }}
                                            />
                                            <br />
                                            <input
                                                type="range"
                                                min={50}
                                                max={500}
                                                value={z.height}
                                                onChange={e => updateZone(z.id, { height: num(e.target.value) })}
                                                style={{ width: '400px' }}
                                            />
                                        </div>

                                        <div className="mt-3 d-flex gap-2">
                                            <button className="btn btn-sm btn-warning" onClick={() => addZone('delete', z.id)}>Add Delete Print Area</button>
                                            <button className="btn btn-sm btn-danger" onClick={() => deleteZone(z.id)}>Clear Print Area</button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-3">
                                <label className="form-label">Select Product</label>
                                <select className="form-select" value={productType} onChange={e => setProductType(e.target.value)}>
                                    <option value="">-- Select Product --</option>
                                    {Object.keys(PRODUCT_OPTIONS).map(type => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>
                            </div>

                            {productType && (
                                <div className="mt-3">
                                    <label className="form-label">Select Color</label>
                                    <select className="form-select" value={color} onChange={e => setColor(e.target.value)}>
                                        <option value="">-- Select Color --</option>
                                        {PRODUCT_OPTIONS[productType].map(c => (
                                            <option key={c} value={c}>{c}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {color && (
                                <div className="mt-3" style={{ width: 30, height: 30, backgroundColor: COLOR_HEX[color], border: '1px solid #000' }}></div>
                            )}

                            <div className="mt-3">
                                <button className="btn btn-success" onClick={handleSave}>Save To Database</button>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* final composited canvas (ẩn) */}
            <canvas ref={finalCanvasRef} width={fixedSize.width} height={fixedSize.height} style={{ display: 'none' }} />
        </div>
    );
};

const mapStateToProps = (state) => ({});
const mapDispatchToProps = { mockupCreate };
export default connect(mapStateToProps, mapDispatchToProps)(ImageEditor3D);
